import request from "utils/request";
import { baseRequestURL } from "api/BaseAPI";
import { AibleClientFactory } from "client/aible/factory";
export function getSchemaMonitors() {
    return request(baseRequestURL + "v1/schema_monitors?s=snapshots&s=tenant_id&s=deleted&s=version&s=schema", {
        method: "GET"
    }).then((response) => response);
}
export function getSchemaMonitor(id) {
    return request(baseRequestURL + "v1/schema_monitor/" + id, {
        method: "GET"
    }).then((response) => response);
}
export function patchSchemaMonitor(id, update) {
    return request(baseRequestURL + "v1/schema_monitor/" + id, {
        method: "PATCH",
        body: JSON.stringify(update)
    }).then((response) => response);
}
export function deleteSchemaMonitor(id) {
    return request(baseRequestURL + "v1/schema_monitor/" + id, {
        method: "DELETE"
    }).then((response) => response);
}
export function getSchemaMonitorPresignedUrl(id) {
    return request(baseRequestURL + "v1/schema_monitor/" + id + "/presigned_url", {
        method: "POST"
    }).then((response) => response);
}
export async function createSchemaMonitor(accountId, settings, folderId) {
    const type = settings.type;
    const name = settings.name;
    const newSchemaMonitor = await request(baseRequestURL + "v1/schema_monitors", {
        method: "POST",
        body: JSON.stringify({
            type: type,
            name: name,
            account_id: accountId,
            folder_id: folderId
        })
    });
    return await updateSchemaMonitorAuth(newSchemaMonitor, settings);
}
export async function updateSchemaMonitorAuth(schemaMonitor, settings, forceRefresh = false) {
    const rawSettings = {
        ...settings,
        type: settings.type ? settings.type.toLowerCase() : undefined
    };
    const factory = await AibleClientFactory.getInstance()
        .getAccountClient()
        .getCustomerClientFactory(schemaMonitor.account_id);
    const schemaMonitorClient = factory.getSchemaMonitorClient();
    const credentialsResponse = await schemaMonitorClient.updateCredentials(schemaMonitor.id, rawSettings);
    if (credentialsResponse.type === "incomplete-authorization" && credentialsResponse?.oauth_begin_url) {
        // updateCredentials() can now return a SchemaMonitor or a SchemaMonitorOAuthRequest in which case we need
        // this data returned for use in the reducer.
        return {
            id: schemaMonitor.id,
            isOAuth: true,
            oAuthUrl: credentialsResponse?.oauth_begin_url,
            schemaMonitorId: schemaMonitor?.id
        };
    }
    const finishResult = await finishSchemaMonitorUpdate(schemaMonitor.id, forceRefresh);
    if ("oauth_begin_url" in finishResult) {
        const oAuthRequiredResponse = {
            id: schemaMonitor.id,
            isOAuth: true,
            oAuthUrl: finishResult?.oauth_begin_url,
            schemaMonitorId: schemaMonitor?.id
        };
        return oAuthRequiredResponse;
    }
    return finishResult;
}
export function linkSchemaMonitor(id, type, table, options) {
    const datasetClient = AibleClientFactory.getInstance().getDatasetClient();
    return datasetClient.createDataset({
        schema_monitor_id: id,
        source_content: {
            type: "CONNECTION_METADATA",
            provider: type,
            table: table
        },
        ...options
    });
}
// Handles checking the OAuth window and then patches the monitor afterward
export function handleOAuth(schemaMonitorId, oAuthUrl, oAuthWindow, account) {
    return new Promise((resolve, reject) => {
        const oAuthResponseFail = {
            id: schemaMonitorId,
            isOAuth: true,
            oAuthUrl: oAuthUrl,
            schemaMonitorId: schemaMonitorId
        };
        const cloudUrl = account?.configuration?.cloud_base_url ?? account?.configuration?.lambda_api_endpoint;
        if (!oAuthWindow) {
            reject(new Error("Failed to open OAuth window"));
            resolve({ success: false, oAuthResponse: oAuthResponseFail });
        }
        // Function for updating credentials and refreshing on successful OAuth
        const processAuthorizationSuccess = async () => {
            try {
                const updateOAuthRequest = {
                    status: "UPDATE_CREDENTIALS",
                    url: oAuthUrl
                };
                const patchResponse = await patchSchemaMonitor(schemaMonitorId, updateOAuthRequest);
                if ("id" in patchResponse) {
                    const finishResult = await finishSchemaMonitorUpdate(patchResponse.id);
                    if ("id" in finishResult) {
                        resolve({ success: true });
                    }
                    else {
                        const finishUpdateFailResult = {
                            id: schemaMonitorId,
                            isOAuth: true,
                            oAuthUrl: finishResult?.oauth_begin_url,
                            schemaMonitorId: schemaMonitorId
                        };
                        resolve({ success: false, oAuthResponse: finishUpdateFailResult });
                    }
                }
                else {
                    const patchFailResult = {
                        id: schemaMonitorId,
                        isOAuth: true,
                        oAuthUrl: patchResponse?.oauth_begin_url,
                        schemaMonitorId: schemaMonitorId
                    };
                    resolve({ success: false, oAuthResponse: patchFailResult });
                }
            }
            catch (e) {
                console.log("Failed to update OAuth credentials: ", e);
                resolve({ success: false, oAuthResponse: oAuthResponseFail });
            }
            finally {
                window.removeEventListener("message", handleMessageEvent);
            }
        };
        // Message Handler function for listening to for a successful postMessage from the OAuth window
        const handleMessageEvent = async (e) => {
            if (cloudUrl && e?.origin !== cloudUrl) {
                return;
            }
            if (e?.data?.success && e?.data?.monitor_id !== undefined && e?.data?.expires_at !== undefined) {
                processAuthorizationSuccess();
            }
        };
        window.addEventListener("message", handleMessageEvent);
        const interval = setInterval(async () => {
            // Logic for handling window is closed, checking for incomplete authorization again to reset OAuth required
            if (!oAuthWindow || oAuthWindow.closed) {
                clearInterval(interval);
                try {
                    const schemaMonitor = await getSchemaMonitor(schemaMonitorId);
                    const customerClientFactory = await AibleClientFactory.getInstance()
                        .getAccountClient()
                        .getCustomerClientFactory(schemaMonitor.account_id);
                    const credentials = await customerClientFactory.getSchemaMonitorClient().getCredentials(schemaMonitor.id);
                    // console.log("Window was closed, checking credentials", schemaMonitorId, schemaMonitor);
                    if (credentials?.type === "incomplete-authorization" ||
                        !credentials?.refresh_token ||
                        credentials?.refresh_token === null) {
                        resolve({ success: false, oAuthResponse: oAuthResponseFail });
                    }
                }
                catch {
                    resolve({ success: false, oAuthResponse: oAuthResponseFail });
                }
                finally {
                    window.removeEventListener("message", handleMessageEvent);
                }
            }
        }, 2000);
    });
}
// Lifted processing after a schema monitor is created so that it can be reused after an OAuth authorization.
async function finishSchemaMonitorUpdate(schemaMonitorId, forceRefresh = false) {
    const upload = await getSchemaMonitorPresignedUrl(schemaMonitorId);
    const backendUpdate = {
        url: upload.data_url
    };
    if (forceRefresh) {
        backendUpdate.status = "UPDATE_CREDENTIALS";
    }
    return await patchSchemaMonitor(schemaMonitorId, backendUpdate);
}
